import { type UseInfiniteQueryOptions, type UseInfiniteQueryResult, useInfiniteQuery } from '@tanstack/react-query';

import { useReduxState } from '@/app/shared/context/ReduxStoreContext';

import { useQueryStateAppContext } from '@/packages/query-state/shared/context/appContext';
import { completeSimilarOffersJsonQuerySelector } from '@/packages/redux-state/store/slices/jsonQuery/selectors/smart';
import { getCancellationTokenFromSignal } from '@/packages/utils/getCancellationTokenFromSignal';

import {
  type TGetInfiniteSearchResultDesktopRequest,
  type TGetInfiniteSearchResultDesktopResponse_1,
} from '@/repositories/generated/shared/search-offers/v2/get-infinite-search-result-desktop';

import { type QueryError, type QueryKey, getQueryKey } from './internal/config';
import { fetchGetInfiniteSearchResultDesktopService } from './internal/fetchService';

type UseGetInfiniteSearchResultDesktopQueryOptions<TData> = Omit<
  UseInfiniteQueryOptions<
    TGetInfiniteSearchResultDesktopResponse_1,
    QueryError,
    TData,
    TGetInfiniteSearchResultDesktopResponse_1,
    QueryKey
  >,
  'getNextPageParam'
>;

export function useGetInfiniteSearchResultDesktopQuery<TData = TGetInfiniteSearchResultDesktopResponse_1>(
  getParameters: (page: number) => TGetInfiniteSearchResultDesktopRequest,
  options?: UseGetInfiniteSearchResultDesktopQueryOptions<TData>,
): UseInfiniteQueryResult<TData, QueryError> {
  const applicationContext = useQueryStateAppContext();

  return useInfiniteQuery<TGetInfiniteSearchResultDesktopResponse_1, QueryError, TData, QueryKey>({
    getNextPageParam: data => data.nextPage,
    queryFn: ({ signal, pageParam }) =>
      fetchGetInfiniteSearchResultDesktopService({
        applicationContext,
        cancellationToken: getCancellationTokenFromSignal(signal),
        parameters: getParameters(pageParam),
      }),
    queryKey: getQueryKey(),
    ...options,
  });
}

export function useGetInfiniteSearchResultDesktopByListingQuery(
  options?: UseGetInfiniteSearchResultDesktopQueryOptions<TGetInfiniteSearchResultDesktopResponse_1>,
): UseInfiniteQueryResult<TGetInfiniteSearchResultDesktopResponse_1, QueryError> {
  const reduxState = useReduxState();

  return useGetInfiniteSearchResultDesktopQuery<TGetInfiniteSearchResultDesktopResponse_1>(
    pageParam => ({
      jsonQuery: completeSimilarOffersJsonQuerySelector(reduxState.getState(), pageParam),
    }),
    {
      enabled: false,
      ...options,
    },
  );
}
